import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLogo } from '../../contexts/LogoContext';
import { Menu, X, LogOut, Shield, User, Camera } from 'lucide-react';
import Avatar from '../common/Avatar';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { logoUrl } = useLogo();
  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Leadership', href: '/leadership' },
    { name: 'Team', href: '/team' },
    { name: 'Events', href: '/events' },
    { name: 'Shop', href: '/shop' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Find My Photos', href: '/find-my-photos' },
    { name: 'Get Involved', href: '/get-involved' },
    { name: 'Stories', href: '/stories' },
    { name: 'Contact', href: '/contact' },
  ];


  const socialLinks = [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/beatsofwa/',
      icon: () => (
        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="6" fill="url(#ig-gradient)"/>
          <defs>
            <linearGradient id="ig-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
              <stop stopColor="#f58529"/>
              <stop offset="0.5" stopColor="#dd2a7b"/>
              <stop offset="1" stopColor="#515bd4"/>
            </linearGradient>
          </defs>
          <circle cx="12" cy="12" r="5" stroke="#fff" strokeWidth="2"/>
          <circle cx="18" cy="6" r="1" fill="#fff"/>
        </svg>
      )
    },
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/BeatsOfRedmond/',
      icon: () => (
        <svg viewBox="0 0 24 24" fill="#1877F3" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="6" fill="#1877F3"/>
          <path d="M16.5 8.5h-2a.5.5 0 0 0-.5.5v2h2.5l-.5 2H14v6h-2v-6h-2v-2h2v-1.5A2.5 2.5 0 0 1 14.5 7h2v1.5z" fill="#fff"/>
        </svg>
      )
    },
    {
      name: 'YouTube',
      href: 'https://www.youtube.com/c/BeatsOfRedmond',
      icon: () => (
        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="6" fill="#FF0000"/>
          <polygon points="10,8 16,12 10,16" fill="#fff"/>
        </svg>
      )
    }
  ];

  const isAdmin = (user) => {
    if (!user) return false;
    const role = (user.role || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const adminEmails = [
      'gauravkhandekar007@gmail.com',
      'beatsofredmond@gmail.com',
      'beatsofwashington@gmail.com',
      'admin@bow.org'
    ];
    return role === 'admin' || user.isAdmin === true || adminEmails.includes(email);
  };

  const isActive = (path) => location.pathname === path;

  // Logo is now handled by LogoContext

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-[60]">
      <div className="w-full px-2 sm:px-4">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="BOW Logo" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-base sm:text-lg">B</span>
              )}
            </div>
            <span className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate">
              <span className="inline lg:hidden">BOW</span>
              <span className="hidden lg:inline">Beats of Washington</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 items-center justify-center space-x-6 lg:space-x-8 xl:space-x-10 px-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-xs lg:text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isActive(item.href)
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
            {/* Donation Button - positioned after Contact */}
            <Link
              to="/donate"
              className="bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors duration-200 whitespace-nowrap"
            >
              Donate
            </Link>
          </div>

          {/* Desktop Social Media & Auth Buttons */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4 ml-4">
            {/* Social Media Links */}
            <div className="flex items-center space-x-2 lg:space-x-3 mr-2 lg:mr-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-600 hover:text-primary-600 transition-colors duration-200`}
                  title={social.name}
                >
                  {social.icon()}
                </a>
              ))}
            </div>

            {/* If user is logged in, show profile dropdown */}
            {currentUser ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((open) => !open)}
                  className="flex items-center space-x-1.5 lg:space-x-2 focus:outline-none group"
                >
                  <Avatar 
                    user={currentUser} 
                    size="sm" 
                    className="bg-primary-100"
                  />
                  <span className="text-xs lg:text-sm font-medium text-gray-900 group-hover:text-primary-600 hidden lg:inline truncate max-w-[180px] xl:max-w-[220px]">
                    {currentUser.displayName || 
                     (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : null) ||
                     currentUser.firstName ||
                     currentUser.email}
                  </span>
                  <svg className="w-3 h-3 lg:w-4 lg:h-4 ml-0.5 lg:ml-1 text-gray-400 group-hover:text-primary-600 hidden lg:block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-2xl z-[70] overflow-hidden animate-slide-up backdrop-blur-sm">
                    {/* User Info Header */}
                    <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-orange-500 px-5 py-4">
                      <div className="flex items-center space-x-3">
                        <Avatar 
                          user={currentUser} 
                          size="lg" 
                          className="bg-white/20 border-2 border-white/30 ring-2 ring-white/20"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-white text-base truncate">
                            {currentUser.displayName || 
                             (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : null) ||
                             currentUser.firstName ||
                             currentUser.email?.split('@')[0]}
                          </div>
                          <div className="text-sm text-primary-100 truncate">{currentUser.email}</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="py-2">
                      <button
                        className="w-full flex items-center px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 transition-all duration-150 group"
                        onClick={() => { navigate('/member'); setDropdownOpen(false); }}
                      >
                        <User className="w-5 h-5 mr-3 text-primary-600 group-hover:text-primary-700" /> 
                        <span>Member Portal</span>
                      </button>
                      
                      {(isAdmin(currentUser) || currentUser?.role === 'photographer') && (
                        <button
                          className="w-full flex items-center px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 transition-all duration-150 group"
                          onClick={() => { navigate('/photographer-panel'); setDropdownOpen(false); }}
                        >
                          <Camera className="w-5 h-5 mr-3 text-orange-600 group-hover:text-orange-700" /> 
                          <span>Photographer Portal</span>
                        </button>
                      )}

                      {isAdmin(currentUser) && (
                        <button
                          className="w-full flex items-center px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 transition-all duration-150 group border-t border-gray-100 mt-1"
                          onClick={() => { navigate('/admin'); setDropdownOpen(false); }}
                        >
                          <Shield className="w-5 h-5 mr-3 text-primary-600 group-hover:text-primary-700" /> 
                          <span>Admin Portal</span>
                        </button>
                      )}
                      
                      <div className="border-t border-gray-100 my-1"></div>
                      
                      <button
                        className="w-full flex items-center px-5 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-all duration-150 group"
                        onClick={() => { signOut(); setDropdownOpen(false); }}
                      >
                        <LogOut className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" /> 
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 lg:space-x-4">
                <Link to="/login" className="btn-outline text-xs lg:text-sm py-1.5 lg:py-2 px-3 lg:px-4 whitespace-nowrap">
                  Sign In
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-primary-600 focus:outline-none focus:text-primary-600 transition-colors duration-200 p-2 -mr-2"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <div className="relative w-6 h-6">
                <Menu 
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} 
                />
                <X 
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} 
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`md:hidden transition-all duration-300 ease-in-out ${
            isOpen 
              ? 'max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain opacity-100' 
              : 'max-h-0 overflow-hidden opacity-0'
          }`}
        >
          <div className={`px-3 pt-3 pb-8 space-y-2 bg-white border-t border-gray-200 transform transition-transform duration-300 ${
            isOpen ? 'translate-y-0' : '-translate-y-4'
          }`}>
            {/* User Account Section (Placed at top so Member & Admin Portals are immediately visible) */}
            {currentUser ? (
              <div className="pb-3 border-b border-gray-200">
                {/* Mobile User Info Banner */}
                <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-orange-500 rounded-xl px-4 py-3 shadow-md mb-2">
                  <div className="flex items-center space-x-3">
                    <Avatar 
                      user={currentUser} 
                      size="md" 
                      className="bg-white/20 border-2 border-white/30 ring-2 ring-white/20 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-white text-sm sm:text-base truncate">
                        {currentUser.displayName || 
                         (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : null) ||
                         currentUser.firstName ||
                         currentUser.email?.split('@')[0]}
                      </div>
                      <div className="text-xs text-primary-100 truncate">{currentUser.email}</div>
                    </div>
                  </div>
                </div>
                
                {/* User Portal Action Buttons */}
                <div className="space-y-1.5 pt-1">
                  <button
                    className="w-full flex items-center justify-center px-3 py-2.5 text-xs sm:text-sm font-semibold text-gray-800 bg-gray-100 hover:bg-orange-50 hover:text-orange-700 rounded-lg transition-colors border border-gray-200/80 shadow-xs"
                    onClick={() => { navigate('/member'); setIsOpen(false); }}
                  >
                    <User className="w-4 h-4 mr-2 text-primary-600 flex-shrink-0" />
                    <span>Member Portal</span>
                  </button>

                  {(isAdmin(currentUser) || currentUser?.role === 'photographer') && (
                    <button
                      className="w-full flex items-center justify-center px-3 py-2.5 text-xs sm:text-sm font-semibold text-orange-900 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors shadow-xs"
                      onClick={() => { navigate('/photographer-panel'); setIsOpen(false); }}
                    >
                      <Camera className="w-4 h-4 mr-2 text-orange-600 flex-shrink-0" />
                      <span>Photographer Portal</span>
                    </button>
                  )}

                  {isAdmin(currentUser) && (
                    <button
                      className="w-full flex items-center justify-center px-3 py-2.5 text-xs sm:text-sm font-bold text-orange-900 bg-orange-100 hover:bg-orange-200 border border-orange-300 rounded-lg transition-colors shadow-xs"
                      onClick={() => { navigate('/admin'); setIsOpen(false); }}
                    >
                      <Shield className="w-4 h-4 mr-2 text-orange-700 flex-shrink-0" />
                      <span>Admin Portal</span>
                    </button>
                  )}

                  <button
                    className="w-full flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    onClick={() => { signOut(); setIsOpen(false); }}
                  >
                    <LogOut className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="pb-3 border-b border-gray-200">
                <Link
                  to="/login"
                  className="block w-full py-2.5 px-4 text-center font-semibold text-sm text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors border border-primary-200"
                  onClick={() => setIsOpen(false)}
                >
                  Sign In to Account
                </Link>
              </div>
            )}

            {/* Navigation Links */}
            <div className="space-y-0.5 pt-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50 font-semibold border-l-4 border-primary-600'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Donation Button */}
            <Link
              to="/donate"
              className="block mx-1 my-2 bg-gradient-to-r from-primary-600 to-orange-500 hover:from-primary-700 hover:to-orange-600 text-white text-sm font-bold py-2.5 px-4 rounded-xl text-center shadow-md transition-all"
              onClick={() => setIsOpen(false)}
            >
              Donate
            </Link>

            {/* Mobile Social Media Links */}
            <div className="border-t border-gray-100 pt-3 mt-2 flex items-center justify-between px-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Follow Us</span>
              <div className="flex space-x-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-primary-100 text-gray-700 hover:text-primary-600 transition-colors"
                    onClick={() => setIsOpen(false)}
                    aria-label={social.name}
                  >
                    {social.icon()}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 